/**
 * Integrytas Group - Common Security Module (CSM)
 * use_favorites - the Favorites / Pinning ("Quick Access") system.
 *
 * Data model (snake_case, per house convention):
 *
 *   interface favorite_pin {
 *     page_internal_code: string;   // e.g. "CSM.USER.LIST"
 *     display_text:       string;   // localized menu label
 *     route_path:         string;   // e.g. "/security/users"
 *     pinned_at:          string;   // ISO timestamp
 *   }
 *   interface user_quick_access_prefs {
 *     user_id: number;
 *     pins:    favorite_pin[];      // ordered, most-recent first
 *   }
 *
 * Example persisted JSON (localStorage key `csm.favorites.<user_id>`, mirrored
 * in the csm_user_favorite_page table as the durable source of truth):
 *
 *   {
 *     "user_id": 42,
 *     "pins": [
 *       { "page_internal_code": "CSM.USER.LIST", "display_text": "Users",
 *         "route_path": "/security/users", "pinned_at": "2026-06-11T09:30:00Z" },
 *       { "page_internal_code": "EDS.DASHBOARD", "display_text": "Email Dashboard",
 *         "route_path": "/eds/dashboard", "pinned_at": "2026-06-10T14:05:00Z" }
 *     ]
 *   }
 *
 * The hook is the single source of truth for the UI: it seeds from the session's
 * favorite_page_ids, persists toggles to csm_user_favorite_page (durable) AND a
 * localStorage cache (instant next-load), and exposes the derived pin list.
 */

import { useCallback, useMemo, useState } from 'react';
import { apiDb } from './api_client';
import type { AuthSession, PageAccess } from '../types/auth';

export interface favorite_pin {
  page_internal_code: string;
  display_text: string;
  route_path: string;
  pinned_at: string;
}

export interface user_quick_access_prefs {
  user_id: number;
  pins: favorite_pin[];
}

export interface use_favorites_result {
  pinned_codes: Set<string>;
  quick_access_pins: favorite_pin[];
  is_pinned: (page_internal_code: string) => boolean;
  toggle_pin: (page_internal_code: string) => void;
}

export function use_favorites(
  session: AuthSession,
  menu: PageAccess[],
  page_labels: Record<string, string>,
): use_favorites_result {
  const user_id = session.effective_user.user_id;

  // page_id <-> internal_code maps from the permission-scoped menu.
  const { code_by_id, page_by_code } = useMemo(() => {
    const code_by_id = new Map<number, string>();
    const page_by_code = new Map<string, PageAccess>();
    for (const p of menu) { code_by_id.set(p.page_id, p.page_internal_code); page_by_code.set(p.page_internal_code, p); }
    return { code_by_id, page_by_code };
  }, [menu]);

  const seed = useMemo(() => {
    const codes = new Set<string>();
    for (const id of session.effective_user.favorite_page_ids) {
      const code = code_by_id.get(id);
      if (code) codes.add(code);
    }
    return codes;
  }, [session.effective_user.favorite_page_ids, code_by_id]);

  const [pinned_codes, set_pinned_codes] = useState<Set<string>>(seed);

  const persist_cache = useCallback((codes: Set<string>) => {
    const prefs: user_quick_access_prefs = {
      user_id,
      pins: [...codes].map(code => to_pin(code, page_by_code, page_labels)).filter(Boolean) as favorite_pin[],
    };
    try { localStorage.setItem(`csm.favorites.${user_id}`, JSON.stringify(prefs)); } catch { /* noop */ }
  }, [user_id, page_by_code, page_labels]);

  const toggle_pin = useCallback((page_internal_code: string) => {
    const page = page_by_code.get(page_internal_code);
    if (!page) return;
    const will_pin = !pinned_codes.has(page_internal_code);

    // Optimistic local update + cache.
    set_pinned_codes(prev => {
      const next = new Set(prev);
      if (will_pin) next.add(page_internal_code); else next.delete(page_internal_code);
      persist_cache(next);
      return next;
    });

    // Durable persistence (best-effort; the local cache covers a transient failure).
    void (async () => {
      try {
        if (will_pin) {
          await apiDb.from('csm_user_favorite_page').insert({ user_id, page_id: page.page_id });
        } else {
          await apiDb.from('csm_user_favorite_page').delete().eq('user_id', user_id).eq('page_id', page.page_id);
        }
      } catch { /* the localStorage cache keeps the UI consistent until next load */ }
    })();
  }, [pinned_codes, page_by_code, user_id, persist_cache]);

  const is_pinned = useCallback((code: string) => pinned_codes.has(code), [pinned_codes]);

  const quick_access_pins = useMemo(
    () => [...pinned_codes].map(code => to_pin(code, page_by_code, page_labels)).filter(Boolean) as favorite_pin[],
    [pinned_codes, page_by_code, page_labels],
  );

  return { pinned_codes, quick_access_pins, is_pinned, toggle_pin };
}

function to_pin(code: string, page_by_code: Map<string, PageAccess>, page_labels: Record<string, string>): favorite_pin | null {
  const page = page_by_code.get(code);
  if (!page || !page.route_path) return null;   // skip group folders (no route)
  return {
    page_internal_code: code,
    display_text: page_labels[code] ?? code,
    route_path: page.route_path,
    pinned_at: new Date().toISOString(),
  };
}
